import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Button,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Alert,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle,
  Cancel,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { municipiosService, saudeService, monitoramentoService } from '../services/api';

interface Municipio {
  id: number;
  codigo_ibge: string;
  nome: string;
  ativo: boolean;
  // Parametrizacao do CVAT (NT 30/2025). Ficam nulos ate serem preenchidos aqui.
  porte_populacional?: number | null;
  populacao_ibge?: number | null;
}

interface Conexao {
  id: number;
  municipio_id: number;
  nome: string;
  host: string;
  porta: number;
  banco: string;
  usuario: string;
  esquema?: string;
  ssl: boolean;
  ativo: boolean;
  usa_dwh?: boolean;
  usa_snapshot?: boolean;
  modo_pec?: 'direto' | 'xml';
  ultimo_teste_ok: boolean | null;
  ultimo_teste_em: string | null;
}

const MunicipiosPage: React.FC = () => {
  const navigate = useNavigate();
  const [municipios, setMunicipios] = useState<Municipio[]>([]);
  const [conexoesMap, setConexoesMap] = useState<Map<number, Conexao>>(new Map());
  const [conexoesDwMap, setConexoesDwMap] = useState<Map<number, Conexao>>(new Map());
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadConexoes = useCallback(async () => {
    try {
      const conexoes: Conexao[] = await saudeService.getConexoes(0);
      const map = new Map<number, Conexao>();
      for (const c of conexoes) {
        map.set(c.municipio_id, c);
      }
      setConexoesMap(map);
    } catch {
      // Silently fail - user may not have permission
    }
    try {
      const conexoesDw: Conexao[] = await monitoramentoService.getConexoesDw();
      const mapDw = new Map<number, Conexao>();
      for (const c of conexoesDw) {
        mapDw.set(c.municipio_id, c);
      }
      setConexoesDwMap(mapDw);
    } catch {
      // Silently fail
    }
  }, []);

  const loadMunicipios = useCallback(async () => {
    try {
      const data = await municipiosService.getMunicipios();
      setMunicipios(data);
    } catch {
      setError('Erro ao carregar municípios');
    }
  }, []);

  useEffect(() => {
    loadMunicipios();
    loadConexoes();
  }, [loadMunicipios, loadConexoes]);

  const handleDelete = async (id: number) => {
    if (!window.confirm('Tem certeza que deseja excluir este município?')) return;
    try {
      await municipiosService.deleteMunicipio(id);
      setSuccess('Município excluído com sucesso!');
      loadMunicipios();
      loadConexoes();
      setTimeout(() => setSuccess(''), 3000);
    } catch {
      setError('Erro ao excluir município');
    }
  };

  const handleToggleStatus = async (municipio: Municipio) => {
    try {
      await municipiosService.updateMunicipio(municipio.id, { ativo: !municipio.ativo });
      setSuccess('Status atualizado com sucesso!');
      loadMunicipios();
      setTimeout(() => setSuccess(''), 3000);
    } catch {
      setError('Erro ao atualizar status');
    }
  };

  const renderConexaoStatus = (municipioId: number) => {
    const conexao = conexoesMap.get(municipioId);
    if (!conexao) return <Chip label="Não configurado" size="small" variant="outlined" />;
    if (conexao.ultimo_teste_ok === null) return <Chip label="Não testado" size="small" variant="outlined" />;
    if (conexao.ultimo_teste_ok) return <Chip icon={<CheckCircle />} label={conexao.usa_dwh ? 'DWH' : 'PEC'} size="small" color="success" />;
    return <Chip icon={<Cancel />} label="Falha" size="small" color="error" />;
  };

  const renderConexaoDwStatus = (municipioId: number) => {
    const conexao = conexoesDwMap.get(municipioId);
    if (!conexao) return <Chip label="Não configurado" size="small" variant="outlined" />;
    if (conexao.ultimo_teste_ok === null) return <Chip label="Não testado" size="small" variant="outlined" />;
    if (conexao.ultimo_teste_ok) return <Chip icon={<CheckCircle />} label="Conectado" size="small" color="success" />;
    return <Chip icon={<Cancel />} label="Falha" size="small" color="error" />;
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4">Gerenciar Municípios</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/municipios/novo')}>
          Novo Município
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Código IBGE</TableCell>
              <TableCell>Nome</TableCell>
              <TableCell>Porte</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>e-SUS</TableCell>
              <TableCell>DW</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {municipios.map((municipio) => (
              <TableRow key={municipio.id}>
                <TableCell>{municipio.codigo_ibge}</TableCell>
                <TableCell>{municipio.nome}</TableCell>
                <TableCell>
                  {municipio.porte_populacional ? (
                    <Chip label={`Porte ${municipio.porte_populacional}`} size="small" variant="outlined" />
                  ) : (
                    <Chip label="Não definido" size="small" color="warning" variant="outlined" />
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    label={municipio.ativo ? 'Ativo' : 'Inativo'}
                    size="small"
                    color={municipio.ativo ? 'success' : 'default'}
                    onClick={() => handleToggleStatus(municipio)}
                    sx={{ cursor: 'pointer' }}
                  />
                </TableCell>
                <TableCell>{renderConexaoStatus(municipio.id)}</TableCell>
                <TableCell>{renderConexaoDwStatus(municipio.id)}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" color="primary" onClick={() => navigate(`/municipios/${municipio.id}`)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => handleDelete(municipio.id)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

    </Container>
  );
};

export default MunicipiosPage;
